"use client";

import { useCallback, useState, useEffect, useRef } from "react";

import { useDebouncedCallback } from "./use-debounced-callback";

/**
 * Manages a local text input state synced with a URL param.
 * Uses a pendingSync ref to avoid clobbering characters typed mid-debounce
 * when the URL changes as a result of our own debounced update.
 */
export function useTextInputFilter(
  paramKey: string,
  urlValue: string,
  updateFilter: (key: string, value: string | undefined, options?: { replace?: boolean }) => void,
  debounceMs = 400,
) {
  const [localValue, setLocalValue] = useState(urlValue);
  const pendingSync = useRef(false);

  // Sync local state when URL changes externally (e.g., browser back/forward)
  useEffect(() => {
    if (pendingSync.current) {
      pendingSync.current = false;
      return;
    }
    setLocalValue(urlValue);
  }, [urlValue]);

  const debouncedUpdate = useDebouncedCallback((value: string) => {
    pendingSync.current = true;
    updateFilter(paramKey, value || undefined, { replace: true });
  }, debounceMs);

  const setValue = useCallback(
    (value: string) => {
      setLocalValue(value);
      debouncedUpdate(value);
    },
    [debouncedUpdate],
  );

  const submit = useCallback(
    (value: string) => {
      debouncedUpdate.cancel();
      pendingSync.current = true;
      updateFilter(paramKey, value.trim() || undefined);
    },
    [debouncedUpdate, paramKey, updateFilter],
  );

  const reset = useCallback(() => {
    debouncedUpdate.cancel();
    setLocalValue("");
    pendingSync.current = true;
  }, [debouncedUpdate]);

  const clear = useCallback(() => {
    reset();
    updateFilter(paramKey, undefined);
  }, [paramKey, reset, updateFilter]);

  return {
    value: localValue,
    setValue,
    setLocalValue,
    submit,
    clear,
    reset,
    pendingSync,
  };
}
